import { memo } from 'react';
import { TableRow as MuiTableRow, TableCell, Checkbox, Box, Chip } from '@mui/material';
import { Column } from './types';
import tratativaDeDatas from '../../helpers/tratativaDeDatas';
import { formatarDecimalNumber, formatarValorMonetario } from '../../helpers/masks';
import truncarString from "../../helpers/truncarString";

interface TableRowProps<T> {
    row: T;
    columns: Column<T>[];
    index: number;
    selected: boolean;
    enableSelection: boolean;
    onSelect: (row: T) => void;
    onDoubleClick?: (row: T) => void;
}

function TableRowComponent<T extends Record<string, any>>({
    row,
    columns,
    index,
    selected,
    enableSelection,
    onSelect,
    onDoubleClick
}: TableRowProps<T>) {
    const formatValue = (value: any, type?: string, condition?: (value: any) => boolean): React.ReactNode => {
        if (value == null) return '-';

        switch (type) {
            case 'date':
                return typeof value === 'string' ? tratativaDeDatas(value, true) : value;
            case 'truncated':
                return typeof  value === 'string' ? truncarString(value, 50) : value;
            case 'number':
                return typeof value === 'number' ? formatarDecimalNumber(value, 1) : value;
            case 'monetary':
                return typeof value === 'number' ? formatarValorMonetario(value) : value;
            case 'chip':
                return <Chip 
                    label={value} 
                    size="small" 
                    color={condition && !condition(value) ? 'info' : 'default'}
                    sx={{ 
                        maxWidth: '100%',
                        '& .MuiChip-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }
                    }} 
                />;
            default:
                return value;
        }
    };

    const getCellValue = (column: Column<T>) => {
        const path = String(column.id).split('.');
        return path.reduce((acc: any, curr) => (acc ? acc[curr] : undefined), row);
    };

    return (
        <MuiTableRow
            hover
            selected={selected}
            onDoubleClick={() => onDoubleClick?.(row.id)}
            sx={{ cursor: 'pointer' }}
        >
            <TableCell sx={styles.indexCell}>
                {String(index + 1).padStart(2, '0')}
            </TableCell>

            {enableSelection && (
                <TableCell padding="checkbox">
                    <Checkbox
                        color="primary"
                        checked={selected}
                        onClick={(e) => {
                            e.stopPropagation()
                            onSelect(row)
                        }}
                    />
                </TableCell>
            )}

            {columns.map((column, idx) => {
                const value = getCellValue(column);
                const formattedValue = formatValue(value, column.type, column.condition);

                return (
                    <TableCell
                        key={String(column.id)}
                        align={column.numeric ? 'right' : 'left'}
                        sx={idx === 0 ? styles.firstCell : styles.cell}
                    >
                        <Box sx={styles.cellContent}>
                            {column.component ? column.component(value) : formattedValue}
                        </Box>
                    </TableCell>
                );
            })}
        </MuiTableRow>
    );
}

const styles = {
    indexCell: {
        bgcolor: '#e3e3e3',
        minWidth: 30,
        borderBottom: '1px solid darkgray',
        borderLeft: '1px solid darkgray',
        borderRight: '1px solid darkgray'
    },
    cell: {
        fontSize: 13,
        paddingX: 2,
        borderRight: '1px solid #ccc',
        '&:hover': {
            backgroundColor: 'rgba(27, 94, 32, 0.2)',
            transition: 'background-color 0.2s ease'
        }
    },
    firstCell: {
        fontSize: 13,
        borderRight: '1px solid #ccc',
        '&:hover': {
            backgroundColor: 'rgba(27, 94, 32, 0.2)',
            transition: 'background-color 0.2s ease'
        }
    },
    cellContent: {
        width: 'auto',
        minWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    }
};

export const TableRow = memo(TableRowComponent, (prev, next) => {
    return (
        prev.row === next.row &&
        prev.selected === next.selected &&
        prev.index === next.index
    );
}) as typeof TableRowComponent; 